import ServicePageLayout from './ServicePageLayout';
import { IMG } from '@/content/site';

export default function HealthcareRevenue() {
  return (
    <ServicePageLayout
      seo={{
        title: 'Healthcare Revenue & Billing Services | KTK Advisors',
        description:
          'KTK Advisors helps medical practices and healthcare groups strengthen billing, claims follow-up, denial management, and accounts-receivable performance.',
        path: '/solutions/healthcare-revenue',
      }}
      group="Healthcare Revenue & Billing"
      headline="Help your practice collect more of the revenue it earns."
      lede="Care delivered is revenue earned—but only if the revenue cycle performs. KTK Advisors assesses where the cycle leaks and coordinates suitable specialized support to close the gaps."
      audienceIntro="Built for healthcare organizations of every size."
      audience={[
        'Medical practices and physician groups',
        'Clinics and outpatient facilities',
        'Healthcare groups and management organizations',
        'Hospitals',
        'Skilled nursing facilities',
      ]}
      image={IMG.healthcare}
      imageAlt="Modern glass-fronted medical facility"
      problems={[
        {
          t: 'Rising claim denials',
          d: 'Denials that go unworked become write-offs. Most practices have more recoverable revenue sitting in aging claims than they realize.',
        },
        {
          t: 'Aging accounts receivable',
          d: 'When AR follow-up falls behind, cash flow lags months behind the care already delivered.',
        },
        {
          t: 'Coding and documentation gaps',
          d: 'Undercoding, inconsistent coding, and documentation issues quietly drain revenue from every claim cycle.',
        },
        {
          t: 'Credentialing delays',
          d: 'Provider credentialing bottlenecks delay billing, complicate payer relationships, and create administrative drag.',
        },
        {
          t: 'Staffing strain in the billing office',
          d: 'Turnover, training gaps, and workload spikes make it hard to keep follow-up disciplined week after week.',
        },
      ]}
      capabilities={[
        'Medical billing assessment',
        'Coding support review',
        'Claims submission and follow-up coordination',
        'Denial management',
        'Accounts-receivable follow-up',
        'Credentialing support',
        'Administrative support where appropriate',
        'Revenue cycle performance visibility',
      ]}
      diagram={{
        title: 'The revenue cycle, end to end.',
        steps: [
          { n: '01', label: 'Patient encounter & documentation' },
          { n: '02', label: 'Coding' },
          { n: '03', label: 'Claim submission' },
          { n: '04', label: 'Payer follow-up' },
          { n: '05', label: 'Payment posting' },
          { n: '06', label: 'Denial management & AR follow-up' },
        ],
      }}
      diagramCaption="Every step either protects earned revenue or leaks it. KTK Advisors helps you see which steps are underperforming, then coordinates the right specialized support to fix them."
      helpsIntro="KTK Advisors is your single point of contact—assessing your organization's needs and coordinating suitable specialized support."
      helps={[
        {
          t: 'Assess before recommending',
          d: 'We start with how your cycle actually performs—where claims stall, where denials cluster, where follow-up slips—before proposing anything.',
        },
        {
          t: 'Coordinate the right specialists',
          d: 'Billing, coding, credentialing, and AR support each require specific expertise. KTK coordinates the suitable specialized support for your situation.',
        },
        {
          t: 'Keep one point of contact',
          d: 'You deal with KTK Advisors—clear explanations, plain language, and accountability from first conversation through review.',
        },
      ]}
      faqs={[
        {
          q: 'Are you a billing company?',
          a: 'KTK Advisors is a client-facing advisor. We help assess your organization\'s needs and coordinate suitable specialized support where appropriate. In many cases that means coordinating billing and revenue cycle specialists rather than replacing your current arrangements outright.',
        },
        {
          q: 'Will you replace our billing staff?',
          a: 'Not necessarily. Some organizations engage support for specific segments—denials, aging AR, or credentialing—while keeping their existing team. The right structure depends on what the assessment finds.',
        },
        {
          q: 'Do you need access to patient information?',
          a: 'No. An initial assessment is based on process and performance questions. Please do not send patient information, financial account details, or other sensitive records through the website.',
        },
        {
          q: 'How are fees structured?',
          a: 'It depends on the scope and the support required. Whatever the structure, the terms are explained clearly before you move forward.',
        },
        {
          q: 'How long does an assessment take?',
          a: 'It varies with the size of the organization and the complexity of the arrangements. The first conversation will give you a realistic sense of scope.',
        },
      ]}
      cta={{
        title: 'Discuss your revenue cycle.',
        copy: 'Tell us where your cycle struggles—denials, aging AR, credentialing, or something harder to name. We\'ll help you see what\'s recoverable.',
        button: 'Discuss Your Revenue Cycle',
      }}
      interestParam="healthcare-revenue"
    />
  );
}
